import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import DashboardLayout from '../../layouts/DashboardLayout'
import ChangePassword from './ChangePassword'
import Contact from './Contact'
import Education from './Education'
import Health from './Health'
import Job from './Job'
import Overview from './Overview'
// Styled component
import {
  PageWrappper,
  ProfileContentPanel,
  ProfileInnerCard,
  ProfileLayoutGrid,
  ProfileNavigationButton,
  ProfileNavigationWrapper,
  ProfilePageTitle,
  ProfileSectionTitle,
} from './MyProfilePageStyled'
// Other
import { inject, observer } from 'mobx-react'
import { createBrowserHistory } from 'history'

export const ProfileSection = {
  OVERVIEW: 'overview',
  CONTACT: 'contact',
  WORK: 'work',
  EDUCATION: 'education',
  HEALTH: 'health',
  CHANGE_PASSWORD: 'change-password',
}

export const profileNavigationItems = [
  {
    key: ProfileSection.OVERVIEW,
    label: 'Tổng quan',
  },
  {
    key: ProfileSection.CONTACT,
    label: 'Thông tin liên hệ',
  },
  {
    key: ProfileSection.WORK,
    label: 'Công việc',
  },
  {
    key: ProfileSection.EDUCATION,
    label: 'Trình độ học vấn',
  },
  {
    key: ProfileSection.HEALTH,
    label: 'Sức khỏe',
  },
  {
    key: ProfileSection.CHANGE_PASSWORD,
    label: 'Thay đổi mật khẩu',
  },
]

const findProfileSectionItem = sectionKey =>
  profileNavigationItems.find(item => item.key === sectionKey)

const ProfileNavigation = memo(({ activeSection, items, onSectionChange }) => (
  <ProfileNavigationWrapper aria-label="Điều hướng thông tin cá nhân">
    {items.map(item => (
      <ProfileNavigationButton
        key={item.key}
        type="button"
        $active={activeSection === item.key}
        onClick={() => onSectionChange(item.key)}>
        {item.label}
      </ProfileNavigationButton>
    ))}
  </ProfileNavigationWrapper>
))

ProfileNavigation.propTypes = {
  activeSection: PropTypes.oneOf(Object.values(ProfileSection)).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOf(Object.values(ProfileSection)).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSectionChange: PropTypes.func.isRequired,
}

const ProfileContent = memo(
  ({ sectionTitle, children, useInnerCard = true }) => (
    <ProfileContentPanel>
      <ProfileSectionTitle>{sectionTitle}</ProfileSectionTitle>
      {useInnerCard ? (
        <ProfileInnerCard>{children}</ProfileInnerCard>
      ) : (
        children
      )}
    </ProfileContentPanel>
  )
)

ProfileContent.propTypes = {
  sectionTitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  useInnerCard: PropTypes.bool,
}

const MyProfilePage = props => {
  const { authenticationStore, match } = props
  const { setMustChangePassword } = authenticationStore
  const history = useMemo(() => createBrowserHistory(), [])
  const profileRouteParam = match.params[0]

  const [keyInfoActive, setKeyInfoActive] = useState({
    key: ProfileSection.OVERVIEW,
    label: 'Tổng quan',
  })

  useEffect(() => {
    const key = profileRouteParam?.replace('/', '')
    if (key) {
      const menuItem = findProfileSectionItem(key)
      if (menuItem) {
        setKeyInfoActive({
          key: menuItem.key,
          label: menuItem.label,
        })
      }
    }
  }, [profileRouteParam])

  const handleProfileSectionChange = useCallback(
    section => {
      const selectedItem = findProfileSectionItem(section)

      if (!selectedItem) return

      setKeyInfoActive({
        key: selectedItem.key,
        label: selectedItem.label,
      })

      history.push(`/my-profile/${selectedItem.key}`)
    },
    [history]
  )

  const handleAfterPasswordChanged = useCallback(() => {
    setKeyInfoActive({
      key: ProfileSection.OVERVIEW,
      label: 'Tổng quan',
    })
    setMustChangePassword()
  }, [setMustChangePassword])

  const handleCancelChangePassword = useCallback(() => {
    handleProfileSectionChange(ProfileSection.OVERVIEW)
  }, [handleProfileSectionChange])

  const renderInfo = () => {
    if (keyInfoActive.key === ProfileSection.OVERVIEW) return <Overview />
    if (keyInfoActive.key === ProfileSection.CONTACT) return <Contact />
    if (keyInfoActive.key === ProfileSection.WORK) return <Job />
    if (keyInfoActive.key === ProfileSection.EDUCATION) return <Education />
    if (keyInfoActive.key === ProfileSection.HEALTH) return <Health />
    if (keyInfoActive.key === ProfileSection.CHANGE_PASSWORD)
      return (
        <ChangePassword
          onAfterPasswordChanged={handleAfterPasswordChanged}
          onCancel={handleCancelChangePassword}
        />
      )
  }

  return (
    <DashboardLayout backgroundColor="#ffffff">
      <PageWrappper>
        <ProfilePageTitle>Thông tin của bạn</ProfilePageTitle>
        <ProfileLayoutGrid>
          <ProfileNavigation
            activeSection={keyInfoActive.key}
            items={profileNavigationItems}
            onSectionChange={handleProfileSectionChange}
          />
          <ProfileContent
            sectionTitle={keyInfoActive.label}
            useInnerCard={keyInfoActive.key === ProfileSection.CHANGE_PASSWORD}>
            {renderInfo()}
          </ProfileContent>
        </ProfileLayoutGrid>
      </PageWrappper>
    </DashboardLayout>
  )
}

MyProfilePage.propTypes = {
  authenticationStore: PropTypes.shape({
    setMustChangePassword: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }).isRequired,
}

export default memo(
  inject(
    'authenticationStore',
    'commonStore',
    'loadingAnimationStore'
  )(observer(MyProfilePage))
)
